import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  AtSign, Bell, CheckCheck, CheckSquare2, Clock3, FileUp, FolderKanban, MessageCircle, Pin, PinOff, RefreshCw, X,
} from 'lucide-react';
import { useAuth } from '../../context';
import { apiDismissNotification, apiGetNotifications, apiMarkNotificationRead, apiMarkAllNotificationsRead, apiUpdateNotificationReminder } from '../../api';
import { PersonAvatar } from '../People/PeopleUI';

const timeAgo = (value) => {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value)) / 1000));
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const notificationMeta = (type) => {
  if (type === 'mention') return { label: 'Mention', icon: AtSign, tone: 'mention' };
  if (type === 'new_message' || type === 'milestone_feedback' || type === 'milestone_reply') return { label: 'Message', icon: MessageCircle, tone: 'message' };
  if (type === 'ping_message') return { label: 'Ping', icon: MessageCircle, tone: 'message' };
  if (type === 'todo_assigned' || type === 'milestone_approved') return { label: type === 'milestone_approved' ? 'Approved' : 'To-do', icon: CheckSquare2, tone: 'success' };
  if (type === 'file_uploaded' || type === 'client_file_uploaded' || type === 'upload_request') return { label: 'File', icon: FileUp, tone: 'file' };
  if (type === 'project_created') return { label: 'Project', icon: FolderKanban, tone: 'project' };
  if (type === 'project_updated' || type === 'milestone_updated') return { label: 'Update', icon: RefreshCw, tone: 'update' };
  return { label: 'Activity', icon: Bell, tone: 'general' };
};

const PANEL_WIDTH = 400;
const notificationDestination = (notification) => {
  const link = notification.link || '';
  if (notification.type !== 'new_message' || !link) return link;
  const url = new URL(link, window.location.origin);
  if (/\/projects\/[^/]+$/.test(url.pathname)) url.searchParams.set('tab', 'messages');
  return `${url.pathname}${url.search}${url.hash}`;
};

export default function NotificationBell({ variant = 'sidebar' }) {
  const { jwtToken } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    if (!jwtToken) return;
    try {
      const data = await apiGetNotifications(jwtToken);
      setItems(data.notifications || []);
      setUnread(data.unreadCount || 0);
    } catch { /* Keep the last successful notification list. */ }
  }, [jwtToken]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, 30000);
    const sync = (event) => {
      const { id, changes, dismissed } = event.detail || {};
      if (!id) { load(); return; }
      setItems((current) => dismissed ? current.filter((item) => item._id !== id) : current.map((item) => item._id === id ? { ...item, ...(changes || {}) } : item));
    };
    window.addEventListener('notifications:changed', sync);
    return () => { clearInterval(timer); window.removeEventListener('notifications:changed', sync); };
  }, [load]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      if (variant === 'sidebar') {
        setPos({ left: Math.min(rect.right + 10, window.innerWidth - PANEL_WIDTH - 8), bottom: Math.max(8, window.innerHeight - rect.bottom) });
      } else if (variant === 'bottomBar') {
        setPos({ right: Math.max(8, window.innerWidth - rect.right), bottom: window.innerHeight - rect.top + 8 });
      } else {
        setPos({ right: Math.max(8, window.innerWidth - rect.right), top: rect.bottom + 8 });
      }
    }
    setOpen((value) => !value);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (btnRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const openItem = async (notification) => {
    setOpen(false);
    if (!notification.read) {
      setUnread((count) => Math.max(0, count - 1));
      setItems((current) => current.map((item) => (item._id === notification._id ? { ...item, read: true } : item)));
      try { await apiMarkNotificationRead(jwtToken, notification._id); } catch { load(); }
    }
    const destination = notificationDestination(notification);
    if (destination) navigate(destination);
  };

  const markAll = async () => {
    setUnread(0);
    setItems((current) => current.map((item) => ({ ...item, read: true })));
    try { await apiMarkAllNotificationsRead(jwtToken); } catch { load(); }
  };
  const updateReminder = async (notification, payload) => {
    setItems((current) => current.map((item) => item._id === notification._id ? { ...item, ...payload } : item));
    window.dispatchEvent(new CustomEvent('notifications:changed', { detail: { id: notification._id, changes: payload } }));
    try { await apiUpdateNotificationReminder(jwtToken, notification._id, payload); await load(); } catch { load(); }
  };
  const dismiss = async (notification) => {
    setItems((current) => current.filter((item) => item._id !== notification._id));
    if (!notification.read) setUnread((count) => Math.max(0, count - 1));
    window.dispatchEvent(new CustomEvent('notifications:changed', { detail: { id: notification._id, dismissed: true } }));
    try { await apiDismissNotification(jwtToken, notification._id); } catch { load(); }
  };
  const pinnedCount = items.filter((notification) => notification.pinned).length;

  const panel = open && pos ? ReactDOM.createPortal(
    <section ref={panelRef} className={`notification-dropdown notification-dropdown-${variant}`} style={pos} aria-label="Notifications">
      <header className="notification-dropdown-header">
        <span className="notification-dropdown-title-icon"><Bell size={17} /></span>
        <div className="notification-dropdown-title">
          <strong>Notifications</strong>
          <span>{unread > 0 ? `${unread} unread ${unread === 1 ? 'update' : 'updates'}` : 'You’re all caught up'}</span>
        </div>
        {unread > 0 && <button type="button" className="notification-mark-all" onClick={markAll}><CheckCheck size={14} />Mark all read</button>}
      </header>

      <div className="notification-dropdown-feed scroll-hidden">
        {items.length === 0 ? (
          <div className="notification-dropdown-empty"><span><Bell size={20} /></span><strong>No new notifications</strong><p>New project activity will appear here.</p></div>
        ) : items.map((notification) => {
          const meta = notificationMeta(notification.type);
          const Icon = meta.icon;
          return (
            <div key={notification._id} className={`notification-dropdown-item ${notification.read ? 'read' : 'unread'} ${notification.pinned ? 'pinned' : ''}`}>
              <button type="button" className="notification-dropdown-open" onClick={() => openItem(notification)}>
              {notification.type === 'ping_message' && notification.actor
                ? <PersonAvatar person={notification.actor} size="sm" unread={!notification.read} />
                : <span className={`notification-dropdown-item-icon ${meta.tone}`}><Icon size={16} /></span>}
              <span className="notification-dropdown-item-copy">
                <span className="notification-dropdown-item-meta"><strong>{meta.label}</strong><time>{timeAgo(notification.createdAt)}</time></span>
                <span className="notification-dropdown-item-text">{notification.text}</span>
              </span>
              {!notification.read && <span className="notification-dropdown-unread" aria-label="Unread" />}
              </button>
              <span className="notification-dropdown-actions"><button type="button" onClick={() => updateReminder(notification, { pinned: !notification.pinned, remindAt: null })} title={notification.pinned ? 'Unpin reminder' : 'Pin reminder'}>{notification.pinned ? <PinOff size={13} /> : <Pin size={13} />}</button><button type="button" onClick={() => updateReminder(notification, { pinned: true, remindAt: new Date(Date.now() + 60 * 60 * 1000).toISOString() })} title="Remind me in one hour"><Clock3 size={13} /></button><button type="button" onClick={() => dismiss(notification)} title="Dismiss notification"><X size={13} /></button></span>
            </div>
          );
        })}
      </div>
      <footer className="notification-dropdown-footer">Notifications update automatically</footer>
    </section>,
    document.body,
  ) : null;

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} title="Notifications" aria-label="Notifications" aria-haspopup="true" aria-expanded={open} className={`notification-bell-trigger ${open ? 'active' : ''}`}>
        <Bell size={18} />
        {unread > 0 && <span className="notification-bell-count">{unread > 9 ? '9+' : unread}</span>}
        {pinnedCount > 0 && <span className="notification-pinned-count" aria-label={`${pinnedCount} pinned notification${pinnedCount === 1 ? '' : 's'}`}><Pin size={9} fill="currentColor" />{pinnedCount > 9 ? '9+' : pinnedCount}</span>}
      </button>
      {panel}
    </>
  );
}
